// Per-route SEO helpers. This is a single-page React app served via the
// Cloudflare Worker — every route shares the same index.html. By default
// that means every URL inherits the homepage <title>, <meta description>,
// <link rel="canonical">, and Open Graph tags, which both confuses search
// engines (they see the same page over and over) and gives bad social
// previews when someone shares /privacy or /stats.
//
// useMeta() runs in a useEffect, updates the relevant <meta> nodes when a
// route mounts, and restores the originals on unmount so navigating back
// to / cleanly picks up the baked-in homepage metadata.

import { useEffect } from "react";
import { DEFAULT_OG_IMAGE, SITE_URL } from "./routeMeta.js";

// Build the absolute URL for a route given a path-or-absolute string. Passing
// the full URL keeps the helper composable when the caller already has one.
function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE_URL + (pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`);
}

// Idempotent: finds or creates the <meta>/<link> node by selector, sets the
// given attribute. Returns a cleanup function that restores the prior value.
function setMeta(selector, makeNode, attr, value) {
  let node = document.head.querySelector(selector);
  const created = !node;
  let prev = "";
  if (!node) {
    node = makeNode();
    document.head.appendChild(node);
  } else {
    prev = node.getAttribute(attr) || "";
  }
  node.setAttribute(attr, value);
  return () => {
    if (created) {
      node.remove();
    } else {
      node.setAttribute(attr, prev);
    }
  };
}

// Apply per-route metadata.
//
// opts:
//   title       - full <title> string; we don't append the site name
//   description - <meta name="description"> (also used for og:description)
//   path        - route path ("/privacy") or absolute URL; defaults to
//                 window.location for callers that don't want to be explicit
//   ogType      - "website" (default) or "article" for the bonus reader
//   ogImage     - absolute URL of social-card image; defaults to greg-yankees
//   jsonLd      - structured data (an object) for a <script type="ld+json">;
//                 worker.js serves the same thing to crawlers (routeMeta.js)
//   robots      - a <meta name="robots"> value ("noindex, follow"), or ""
export function useMeta({
  title,
  description,
  path,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd = null,
  robots = "",
} = {}) {
  useEffect(() => {
    const resolvedPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const url = absoluteUrl(resolvedPath);

    const prevTitle = document.title;
    if (title) document.title = title;

    const cleanups = [];

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.route = "1";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
      cleanups.push(() => script.remove());
    }
    if (robots) {
      cleanups.push(
        setMeta(
          'meta[name="robots"]',
          () => Object.assign(document.createElement("meta"), { name: "robots" }),
          "content",
          robots,
        ),
      );
    }

    if (description) {
      cleanups.push(
        setMeta(
          'meta[name="description"]',
          () => Object.assign(document.createElement("meta"), { name: "description" }),
          "content",
          description,
        ),
      );
      cleanups.push(
        setMeta(
          'meta[property="og:description"]',
          () => {
            const m = document.createElement("meta");
            m.setAttribute("property", "og:description");
            return m;
          },
          "content",
          description,
        ),
      );
      cleanups.push(
        setMeta(
          'meta[name="twitter:description"]',
          () => Object.assign(document.createElement("meta"), { name: "twitter:description" }),
          "content",
          description,
        ),
      );
    }

    cleanups.push(
      setMeta(
        'link[rel="canonical"]',
        () => {
          const l = document.createElement("link");
          l.setAttribute("rel", "canonical");
          return l;
        },
        "href",
        url,
      ),
    );
    cleanups.push(
      setMeta(
        'meta[property="og:url"]',
        () => {
          const m = document.createElement("meta");
          m.setAttribute("property", "og:url");
          return m;
        },
        "content",
        url,
      ),
    );
    cleanups.push(
      setMeta(
        'meta[property="og:type"]',
        () => {
          const m = document.createElement("meta");
          m.setAttribute("property", "og:type");
          return m;
        },
        "content",
        ogType,
      ),
    );
    cleanups.push(
      setMeta(
        'meta[property="og:image"]',
        () => {
          const m = document.createElement("meta");
          m.setAttribute("property", "og:image");
          return m;
        },
        "content",
        ogImage,
      ),
    );
    cleanups.push(
      setMeta(
        'meta[name="twitter:image"]',
        () => Object.assign(document.createElement("meta"), { name: "twitter:image" }),
        "content",
        ogImage,
      ),
    );

    if (title) {
      cleanups.push(
        setMeta(
          'meta[property="og:title"]',
          () => {
            const m = document.createElement("meta");
            m.setAttribute("property", "og:title");
            return m;
          },
          "content",
          title,
        ),
      );
      cleanups.push(
        setMeta(
          'meta[name="twitter:title"]',
          () => Object.assign(document.createElement("meta"), { name: "twitter:title" }),
          "content",
          title,
        ),
      );
    }

    return () => {
      document.title = prevTitle;
      // Restore prior meta values in reverse order.
      for (let i = cleanups.length - 1; i >= 0; i--) cleanups[i]();
    };
  }, [title, description, path, ogType, ogImage, jsonLd, robots]);
}
